import React from 'react';

export type CardVariant = 'default' | 'parchment' | 'panel' | 'framed' | 'danger';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  headerAction?: React.ReactNode;
  footer?: React.ReactNode;
  showCrosshairs?: boolean;
  borderedHeader?: boolean;
  children: React.ReactNode;
}

export const Card: React.FC<CardProps> = ({
  variant = 'default',
  padding = 'md',
  title,
  subtitle,
  headerAction,
  footer,
  showCrosshairs = false,
  borderedHeader = true,
  className = '',
  children,
  ...props
}) => {
  const variantStyles: Record<CardVariant, string> = {
    default: 'bg-white border border-paper-400 shadow-paper',
    parchment: 'bg-paper-100 border border-paper-400 shadow-paper-sm',
    panel: 'bg-paper-200 border border-paper-300',
    framed: 'bg-white border-2 border-ink-800 shadow-paper-lg',
    danger: 'bg-red-50/30 border-2 border-red-700/80 shadow-paper-sm',
  };

  const paddingStyles: Record<CardPadding, string> = {
    none: 'p-0',
    sm: 'p-3 sm:p-4',
    md: 'p-4 sm:p-6',
    lg: 'p-6 sm:p-8',
  };

  const hasHeader = Boolean(title || subtitle || headerAction);

  return (
    <div
      className={`
        relative rounded-xs transition-shadow duration-150
        ${variantStyles[variant]}
        ${showCrosshairs ? 'doc-crosshair-tl doc-crosshair-br' : ''}
        ${className}
      `}
      {...props}
    >
      {/* Header Slot */}
      {hasHeader && (
        <div
          className={`
            px-4 sm:px-6 pt-4 sm:pt-5 pb-3 flex items-start justify-between gap-4
            ${borderedHeader ? 'border-b border-paper-300' : ''}
          `}
        >
          <div className="space-y-0.5 min-w-0">
            {typeof title === 'string' ? (
              <h3 className="font-serif text-base sm:text-lg font-bold tracking-wider text-ink-900 uppercase truncate">
                {title}
              </h3>
            ) : (
              title
            )}
            {subtitle && (
              <p className="font-serif text-xs text-ink-500 tracking-wide">
                {subtitle}
              </p>
            )}
          </div>
          {headerAction && (
            <div className="shrink-0 flex items-center gap-2">
              {headerAction}
            </div>
          )}
        </div>
      )}

      {/* Main Content Body */}
      <div className={paddingStyles[padding]}>{children}</div>

      {/* Footer Slot */}
      {footer && (
        <div className="px-4 sm:px-6 py-3 border-t border-paper-300 bg-paper-50/50 rounded-b-xs text-xs text-ink-500 font-serif">
          {footer}
        </div>
      )}
    </div>
  );
};
